// The filter bar of the facility lists (Tiendas, Observaciones): search,
// one multi-select chip per column with counts against the OTHER filters,
// «Limpiar filtros» (only with a filter active), an actions slot and
// «N resultados», plus the list's role=status announcer. Controlled: the
// state lives in the URL and the route that mounts it owns it.
//
// Same markup as the postventa bar (ObservacionesFiltros) — `.filters`,
// `.filter-search`, FilterMultiDropdown, `.filter-spacer` — so it needs
// no CSS of its own.
import { useRef, type ReactNode } from "react";
import { EmptyState } from "../EmptyState";
import { FilterMultiDropdown } from "../FilterMultiDropdown";
import { Icon } from "../icons";
import type { FilterOption } from "../../lib/facility/listState";

export type FacilityChip = {
  key: string;
  label: string;
  options: readonly FilterOption[];
  value: readonly string[];
};

export function FacilityFiltros({
  searchPlaceholder,
  q,
  onSearch,
  chips,
  onChip,
  active,
  onClear,
  actions,
  resultCount,
  live,
}: {
  searchPlaceholder: string;
  q: string;
  onSearch: (q: string) => void;
  chips: readonly FacilityChip[];
  onChip: (key: string, next: string[]) => void;
  /** Any filter or search on: shows «Limpiar filtros». */
  active: boolean;
  onClear: () => void;
  actions?: ReactNode;
  resultCount: number;
  /** The announcer's current message (useListAnnouncer). */
  live: string;
}) {
  const searchRef = useRef<HTMLInputElement>(null);
  return (
    <div className="filters">
      <div className="filter-search">
        <Icon.Search />
        <input
          ref={searchRef}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder.replace(/…$/, "")}
          value={q}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      {chips.map((c) => (
        <FilterMultiDropdown
          key={c.key}
          label={c.label}
          options={c.options}
          value={c.value}
          onChange={(next) => onChip(c.key, next)}
        />
      ))}
      {active && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            onClear();
            // The button unmounts with the filters; the search box is the
            // natural next stop.
            searchRef.current?.focus();
          }}
        >
          Limpiar filtros
        </button>
      )}
      <div className="filter-spacer" />
      {actions}
      <span className="filter-sort">
        {resultCount} {resultCount === 1 ? "resultado" : "resultados"}
      </span>
      <p role="status" aria-live="polite" className="sr-only">
        {live}
      </p>
    </div>
  );
}

/** The empty state of a list whose filters left no row. */
export function FiltrosSinCoincidencias({
  noun,
  onClear,
}: {
  /** «tienda» / «observación». */
  noun: string;
  onClear: () => void;
}) {
  return (
    <EmptyState
      title={`Ninguna ${noun} coincide con tus filtros`}
      description="Prueba quitando alguno para ver más resultados."
      action={
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClear}>
          Limpiar filtros
        </button>
      }
    />
  );
}
